import React from 'react';
import { colors } from '../styles/colors';

interface SlotInputProps {
  label: string;
  value: number;
  onChange: (value: number) => void;
  tooltip?: string;
}

const SlotInput: React.FC<SlotInputProps> = ({ label, value, onChange, tooltip }) => {
  const getDisplayValue = () => {
    if (value === -1) return '';
    return value.toString();
  };

  const getStatusText = () => {
    if (value === -1) return 'Não incluído no plano';
    if (value === 0) return 'Ilimitado';
    return `${value} ${value === 1 ? 'slot' : 'slots'}`;
  };

  const getStatusColor = () => {
    if (value === -1) return colors.error;
    if (value === 0) return colors.success;
    return colors.primary;
  };

  const handleToggleIncluded = () => {
    if (value === -1) {
      onChange(0); // Muda para ilimitado
    } else {
      onChange(-1); // Muda para não incluído
    }
  };

  const handleValueChange = (newValue: string) => {
    const num = parseInt(newValue) || 0;
    if (num < 0) return; // Não permite negativos diretos
    onChange(num);
  };

  const handleInputKeyDown = (e: React.KeyboardEvent) => {
    // Impede entrada de valores negativos
    if (e.key === '-' || e.key === 'e' || e.key === 'E') {
      e.preventDefault();
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.labelRow}>
        <label style={styles.label}>{label}</label>
        {tooltip && <span style={styles.tooltip} title={tooltip}>?</span>}
      </div>
      
      <div style={styles.controls}>
        <label style={styles.checkboxLabel}>
          <input
            type="checkbox"
            checked={value !== -1}
            onChange={handleToggleIncluded}
          />
          <span style={{marginLeft: '8px'}}>Incluído no plano</span>
        </label>
        
        {value !== -1 && (
          <div style={styles.inputRow}>
            <input
              type="number"
              min="0"
              value={getDisplayValue()}
              onChange={(e) => handleValueChange(e.target.value)}
              onKeyDown={handleInputKeyDown}
              style={styles.input}
              placeholder="0 = Ilimitado"
            />
            <span style={{...styles.status, color: getStatusColor()}}>
              {getStatusText()}
            </span>
          </div>
        )}
        
        {value === -1 && (
          <div style={{...styles.status, color: getStatusColor(), marginTop: '8px'}}>
            {getStatusText()}
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: {
    marginBottom: '16px',
  },
  labelRow: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '8px',
  },
  label: {
    fontSize: '14px',
    color: colors.text,
    fontWeight: '500' as const,
  },
  tooltip: {
    marginLeft: '8px',
    width: '16px',
    height: '16px',
    borderRadius: '50%',
    backgroundColor: colors.textLight,
    color: 'white',
    fontSize: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'help',
  },
  controls: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    fontSize: '14px',
    color: colors.text,
  },
  inputRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  input: {
    width: '100px',
    padding: '8px',
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    fontSize: '14px',
    fontFamily: 'inherit',
  },
  status: {
    fontSize: '12px',
    fontWeight: '500' as const,
  },
};

export default SlotInput;